import React, {
	Ref,
	HTMLAttributes,
	forwardRef,
	useMemo,
	ReactNode,
	useState,
	useEffect,
	useImperativeHandle,
	PropsWithChildren,
} from 'react';
import { classNames } from 'primereact/utils';
import { universalRenderer } from 'utils/universalRenderer';
import { Icon, IconSet, IconStyle } from '../Icon';

import './MessageBox.scss';

type MessageBoxVariant =
	| 'success'
	| 'rose'
	| 'purple'
	| 'lightpurple'
	| 'warn'
	| 'blue'
	| 'blue-questions'
	| 'pink'
	| 'pink-questions'
	| 'blank';

export type MessageBoxProps = PropsWithChildren<
	Pick<HTMLAttributes<HTMLDivElement>, 'className'> & {
		icon?: ReactNode | (() => ReactNode);
		iconStyle?: IconStyle;
		iconSet?: IconSet;
		title?: ReactNode;
		message?: ReactNode | ReactNode[];
		variant?: MessageBoxVariant;
		highlight?: boolean;
		autoHide?: boolean;
		autoHideDuration?: number;
		tiny?: boolean;
	}
>;

export interface MessageBoxRef {
	show: () => void;
	hide: () => void;
}

export const MessageBox = forwardRef(
	(
		{
			icon,
			iconStyle,
			iconSet,
			title,
			message,
			variant = 'rose',
			className,
			highlight = false,
			autoHide = false,
			autoHideDuration = 3000,
			tiny = false,
			children,
			...restProps
		}: MessageBoxProps,
		ref: Ref<HTMLDivElement | MessageBoxRef>
	) => {
		const [isVisible, setIsVisible] = useState(!autoHide);

		useImperativeHandle(
			ref,
			() => ({
				show: () => setIsVisible(true),
				hide: () => setIsVisible(false),
			}),
			[]
		);

		useEffect(() => {
			if (autoHide && isVisible) {
				const timer = setTimeout(() => {
					setIsVisible(false);
				}, autoHideDuration);

				return () => clearTimeout(timer);
			}
			return undefined;
		}, [autoHide, autoHideDuration, isVisible]);

		const finalClassName = useMemo(
			() =>
				classNames(
					'message-box',
					`message-box-${variant}`,
					{ 'message-box-highlight': highlight, 'message-box-tiny': tiny },
					className
				),
			[variant, highlight, tiny, className]
		);

		const renderIcon = () => {
			if (icon) {
				if (typeof icon === 'string') {
					return <Icon name={icon} iconStyle={iconStyle} iconSet={iconSet} className="message-box-icon" />;
				}
				return <span className="message-box-icon">{universalRenderer(icon)}</span>;
			}
			return null;
		};

		return (title || message || children) && isVisible ? (
			<div className={finalClassName} {...restProps}>
				<div className="message-box-inside">
					{!title && renderIcon()}
					<div className="message-box-content">
						{title && (
							<div className="message-box-title">
								{renderIcon()}
								<span>{title}</span>
							</div>
						)}
						{Array.isArray(message) ? (
							message.map((text, i) => (
								// eslint-disable-next-line react/no-array-index-key
								<div className="message-box-text" key={i}>
									{text}
								</div>
							))
						) : (
							<div className="message-box-text">{message || children}</div>
						)}
					</div>
				</div>
			</div>
		) : null;
	}
);
